import { getCurrentInstance, onMounted, watch } from 'vue'
import type { Ref } from 'vue'
import store from '@/store/index'
import useRouter from '@/hooks/useRouter'
import { useProject } from '@/hooks/useProject'
import { getInstance } from '@/auth/authWrapper'
import {
    setSuppressPublicMarketplaceRedirect,
    shouldSuppressPublicMarketplaceRedirect,
} from '@/helpers/redirectAfterOnboarding'
import {
    hasAtLeastOneProject,
    isPublicMarketplacePath,
    redirectAuthenticatedUserToProjectMarketplace,
    waitForProjectsLoaded,
} from './marketplaceRouteGuards'

const waitForAuthReady = (authInstance: ReturnType<typeof getInstance>) =>
    new Promise<void>((resolve) => {
        if (!authInstance.loading) {
            resolve()
            return
        }
        const unwatch = authInstance.$watch('loading', (loading: boolean) => {
            if (!loading) {
                unwatch()
                resolve()
            }
        })
    })

type SyncOptions = {
    /** На project-маршруте: не показывать спиннер, если основной контент уже в store */
    hasCachedData?: () => boolean
}

/** Редирект с публичного /marketplace/* на проект и загрузка данных при смене контекста. */
export function usePublicMarketplacePageSync(
    loadPage: () => Promise<void>,
    loading: Ref<boolean>,
    options?: SyncOptions,
) {
    const { project } = useProject()
    const { route, router } = useRouter()
    const root = getCurrentInstance()!.proxy as { $auth?: { isAuthenticated: boolean; loading: boolean } }
    let syncPromise: Promise<void> | null = null
    let pendingResync = false

    const tryRedirectAuthenticatedFromPublicMarketplace = async (): Promise<boolean> => {
        if (project.value || !isPublicMarketplacePath(route.value.path)) return false
        if (shouldSuppressPublicMarketplaceRedirect()) return false

        const authInstance = getInstance()
        await waitForAuthReady(authInstance)
        if (!authInstance.isAuthenticated || !hasAtLeastOneProject()) return false

        return redirectAuthenticatedUserToProjectMarketplace(
            route.value.fullPath,
            router,
            {
                name: route.value.name,
                params: route.value.params as Record<string, string>,
                query: route.value.query as Record<string, any>,
            },
        )
    }

    const runSync = async () => {
        const isPublic = isPublicMarketplacePath(route.value.path)
        const showLoader = isPublic || !(options?.hasCachedData?.() ?? false)

        if (showLoader) {
            loading.value = true
        }
        try {
            if (isPublic) {
                const authInstance = getInstance()
                await waitForAuthReady(authInstance)
                if (authInstance.isAuthenticated) {
                    await waitForProjectsLoaded()
                    if (await tryRedirectAuthenticatedFromPublicMarketplace()) {
                        pendingResync = true
                        return
                    }
                }
            }
            await loadPage()
        } finally {
            if (showLoader) {
                loading.value = false
            }
        }
    }

    const syncPage = () => {
        if (syncPromise) {
            pendingResync = true
            return syncPromise
        }
        syncPromise = runSync().finally(() => {
            syncPromise = null
            if (pendingResync) {
                pendingResync = false
                syncPage()
            }
        })
        return syncPromise
    }

    watch(
        () => {
            if (isPublicMarketplacePath(route.value.path)) {
                return [
                    route.value.fullPath,
                    root.$auth?.isAuthenticated,
                    root.$auth?.loading,
                    store.state.projects?.length,
                ] as const
            }
            return [route.value.fullPath] as const
        },
        syncPage,
    )

    onMounted(() => {
        setSuppressPublicMarketplaceRedirect(false)
        syncPage()
    })

    return { tryRedirectAuthenticatedFromPublicMarketplace, syncPage }
}
